import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, ImageIcon } from 'lucide-react';
import { adminProducts } from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || '';
const imgSrc = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_URL}${url}`;
};

const fmt = (v) => (v == null ? '—' : `$${parseFloat(v).toFixed(2)}`);

// ── AddFeaturedModal ──────────────────────────────────────────────────────────
// Props:
//   existingIds (number[]) — productIDs already featured
//   onAdd(product)         — called with the selected product object
//   onClose()              — close the modal
export default function AddFeaturedModal({ existingIds, onAdd, onClose }) {
  const [search, setSearch]   = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const debounceRef = useRef(null);

  const doSearch = useCallback((q) => {
    setLoading(true);
    adminProducts
      .list({ wholesaleOnly: 'true', ...(q.trim() ? { search: q.trim() } : {}) })
      .then((res) => { if (res.data?.success) setResults(res.data.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Initial load
  useEffect(() => { doSearch(''); }, [doSearch]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const atLimit = existingIds.length >= 8;

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,.45)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">

          {/* Header */}
          <div className="modal-header">
            <h5 className="modal-title">Add Featured Product</h5>
            <button className="btn-close" onClick={onClose} />
          </div>

          {/* Body */}
          <div className="modal-body">
            {atLimit && (
              <div className="alert alert-warning py-2 small mb-3">
                You have 8 featured products. Remove one before adding another.
              </div>
            )}

            {/* Search */}
            <div className="input-group input-group-sm mb-3">
              <span className="input-group-text">
                <Search size={14} />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, varietal, vintage…"
                value={search}
                onChange={handleSearchChange}
                autoFocus
              />
            </div>

            {/* Results */}
            {loading ? (
              <div className="text-center py-4">
                <span className="spinner-border spinner-border-sm text-secondary" />
              </div>
            ) : results.length === 0 ? (
              <div className="text-center text-muted py-4 small">No wholesale products found.</div>
            ) : (
              <div className="list-group list-group-flush" style={{ maxHeight: 360, overflowY: 'auto' }}>
                {results.map((p) => {
                  const alreadyIn = existingIds.includes(p.productID);
                  const disabled  = alreadyIn || atLimit;

                  return (
                    <button
                      key={p.productID}
                      type="button"
                      className={`list-group-item list-group-item-action d-flex align-items-center gap-3 py-2${disabled ? ' disabled' : ''}`}
                      disabled={disabled}
                      style={{ cursor: disabled ? 'default' : 'pointer', opacity: alreadyIn ? 0.55 : 1 }}
                      onClick={() => { if (!disabled) { onAdd(p); onClose(); } }}
                    >
                      {/* Thumbnail */}
                      {imgSrc(p.proLabelImageUrl) ? (
                        <img
                          src={imgSrc(p.proLabelImageUrl)}
                          alt=""
                          style={{ width: 32, height: 45, objectFit: 'cover', borderRadius: 3, flexShrink: 0 }}
                        />
                      ) : (
                        <div
                          className="bg-light d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: 32, height: 45, borderRadius: 3 }}
                        >
                          <ImageIcon size={12} className="text-muted" />
                        </div>
                      )}

                      {/* Info */}
                      <div className="flex-grow-1 text-start">
                        <div className="fw-semibold small lh-sm">{p.proFanciful || '—'}</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {[p.proVintage, p.proVarietal].filter(Boolean).join(' · ')}
                          {p.wholesalePrice != null && ` · ${fmt(p.wholesalePrice)}`}
                        </div>
                      </div>

                      {alreadyIn && (
                        <span className="badge bg-secondary ms-auto">Already featured</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button className="btn btn-secondary btn-sm" onClick={onClose}>
              Cancel
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
